import { useEffect } from 'react';

import type { ActionItemsManagementSnapshot } from '@/backend/contracts/action-items-client';
import { useAppStore } from '@/store/useAppStore';

import {
    clearPendingActionItemMutation,
    clearPendingActionItemMutations,
    listPendingActionItemMutations,
    rollbackPendingActionItemMutation,
    type ActionItemProjection,
} from '../action-item-pending-writes';
import { subscribeToActionItemsManagement } from '../action-items-api';
import { clearScheduledActionItemPatches } from '../action-item-patch-scheduler';
import { subscribeToProjectWriteResultChanges } from '../project-write-outbox';
import type { ProjectScope } from '../project-scope';

function overlayRows<T extends { id: string }>(remote: readonly T[], optimistic: readonly T[]): T[] {
    const merged = new Map(remote.map(row => [row.id, row]));
    for (const row of optimistic) merged.set(row.id, row);
    return [...merged.values()];
}

function mergeActionItemsManagementWithPending(
    scope: ProjectScope,
    snapshot: ActionItemsManagementSnapshot,
    canReviewDrafts: boolean,
): ActionItemProjection {
    const pending = listPendingActionItemMutations(scope);
    return pending.reduce<ActionItemProjection>((projection, mutation) => ({
        items: overlayRows(projection.items, mutation.optimistic.items),
        events: overlayRows(projection.events, mutation.optimistic.events),
        reviewedEvidenceKeys: canReviewDrafts
            ? [...new Set([...projection.reviewedEvidenceKeys, ...mutation.optimistic.reviewedEvidenceKeys])]
            : [],
    }), {
        items: snapshot.items,
        events: snapshot.events,
        reviewedEvidenceKeys: canReviewDrafts ? snapshot.reviewedEvidenceKeys : [],
    });
}

function acknowledgedIds<T extends { id: string }>(remote: readonly T[], optimistic: readonly T[]): string[] {
    // The resolver owns timestamps, provenance, and other canonical fields.
    // An id match is the acknowledgement boundary; event ids additionally
    // encode their owning operation id, while all families must be present.
    return optimistic.filter(row => remote.some(authoritative => authoritative.id === row.id))
        .map(row => row.id);
}

function acknowledgePending(scope: ProjectScope, snapshot: ActionItemsManagementSnapshot): void {
    if (snapshot.status !== 'ready') return;
    for (const mutation of listPendingActionItemMutations(scope)) {
        const itemIds = acknowledgedIds(snapshot.items, mutation.optimistic.items);
        const eventIds = acknowledgedIds(snapshot.events, mutation.optimistic.events);
        const evidenceKeys = mutation.optimistic.reviewedEvidenceKeys.filter(key => (
            snapshot.reviewedEvidenceKeys.includes(key)
        ));
        const complete = mutation.acknowledgedItemIds.every(id => itemIds.includes(id))
            && mutation.acknowledgedEventIds.every(id => eventIds.includes(id))
            && mutation.acknowledgedEvidenceKeys.every(key => evidenceKeys.includes(key));
        if (complete) clearPendingActionItemMutation(scope, mutation.operationId);
    }
}

/** Restore only the rows owned by a rejected command; unrelated live rows remain intact. */
function rollbackActionItemMutation(scope: ProjectScope, operationId: string): void {
    useAppStore.setState(state => {
        const rollback = rollbackPendingActionItemMutation(scope, operationId, {
            items: state.actionItems,
            events: state.actionItemEvents,
            reviewedEvidenceKeys: state.reviewedActionItemEvidenceKeys,
        });
        if (!rollback) return;
        state.actionItems = rollback.items;
        state.actionItemEvents = rollback.events;
        state.reviewedActionItemEvidenceKeys = rollback.reviewedEvidenceKeys;
    });
    clearPendingActionItemMutation(scope, operationId);
}

function isCurrentProjectSession(scope: ProjectScope): boolean {
    const { cloud, auth } = useAppStore.getState();
    return cloud.enabled
        && cloud.orgId === scope.orgId
        && cloud.projectId === scope.projectId
        && auth.initialized
        && Boolean(auth.user);
}

export function useActionItemsSync(enabled = true, access: 'view' | 'edit' = 'edit'): void {
    const cloudEnabled = useAppStore(state => state.cloud.enabled);
    const orgId = useAppStore(state => state.cloud.orgId);
    const projectId = useAppStore(state => state.cloud.projectId);
    const initialized = useAppStore(state => state.auth.initialized);
    const user = useAppStore(state => state.auth.user);

    useEffect(() => {
        const hydrationIneligible = !enabled || !cloudEnabled || !orgId || !projectId || !initialized || !user;
        useAppStore.setState({
            ...(hydrationIneligible ? { actionItems: [], actionItemEvents: [] } : {}),
            actionItemsManagementStatus: 'loading',
            reviewedActionItemEvidenceKeys: [],
        });
        if (hydrationIneligible) return;
        const scope = { orgId, projectId };
        const unsubscribeResult = subscribeToProjectWriteResultChanges(change => {
            if (change.scope.orgId === orgId && change.scope.projectId === projectId
                && change.operation.feature === 'action_items'
                && (change.result.status === 'rejected' || change.result.status === 'superseded')) {
                rollbackActionItemMutation(scope, change.operation.id);
            }
        });
        let active = true;
        const unsubscribeManagement = subscribeToActionItemsManagement(scope, access, snapshot => {
            if (!active) return;
            const accessSafeSnapshot = access === 'edit'
                ? snapshot
                : { ...snapshot, reviewedEvidenceKeys: [] };
            acknowledgePending(scope, accessSafeSnapshot);
            const merged = mergeActionItemsManagementWithPending(scope, accessSafeSnapshot, access === 'edit');
            useAppStore.setState({
                actionItems: merged.items,
                actionItemEvents: merged.events,
                reviewedActionItemEvidenceKeys: merged.reviewedEvidenceKeys,
                actionItemsManagementStatus: snapshot.status,
            });
        });
        return () => {
            active = false;
            unsubscribeManagement();
            unsubscribeResult();
            // Access and identity changes recreate this subscription without
            // ending the project session; keep its pending mutations and
            // queued patch intact. A project/session teardown must never
            // carry either to the next scope.
            if (!isCurrentProjectSession(scope)) {
                clearScheduledActionItemPatches();
                clearPendingActionItemMutations();
            }
        };
    }, [enabled, cloudEnabled, orgId, projectId, initialized, user, access]);

    useEffect(() => () => {
        clearScheduledActionItemPatches();
        clearPendingActionItemMutations();
    }, []);
}
